import React, { ReactNode } from 'react';
import './App.css';
import { Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import StaffAuthPage from './pages/StaffAuthPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import ProductsCatalogPage from './pages/ProductsCatalogPage';
import PackagesPage from './pages/PackagesPage';
import PackageDetailPage from './pages/PackageDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CustomerAccountPage from './pages/CustomerAccountPage';
import AccountOrdersPage from './pages/AccountOrdersPage';
import AccountReferralsPage from './pages/AccountReferralsPage';
import EmployeeProfilePage from './pages/EmployeeProfilePage';
import WorkspaceAccessPendingPage from './pages/WorkspaceAccessPendingPage';
import EmployeesPage from './features/backoffice/users/employees/pages/EmployeesPage';
import WorkspaceShippingAgentsPage from './features/backoffice/users/shippingAgents/pages/WorkspaceShippingAgentsPage';
import EmployeeDetailsPage from './features/backoffice/users/employees/pages/EmployeeDetailsPage';
import CustomersPage from './features/backoffice/users/customers/pages/CustomersPage';
import CustomerDetailPage from './features/backoffice/users/customers/pages/CustomerDetailPage';
import CustomerArchivesPage from './features/backoffice/users/customers/pages/CustomerArchivesPage';
import CustomerArchiveDetailPage from './features/backoffice/users/customers/pages/CustomerArchiveDetailPage';
import RolesPage from './features/backoffice/users/roles/pages/RolesPage';
import RolePermissionsPage from './features/backoffice/users/roles/pages/RolePermissionsPage';
import ProductsPage from './features/backoffice/catalog/products/pages/ProductsPage';
import ExpiredProductsPage from './features/backoffice/catalog/expired-products/pages/ExpiredProductsPage';
import CategoriesPage from './features/backoffice/catalog/categories/pages/CategoriesPage';
import WorkspacePackagesPage from './features/backoffice/catalog/packages/pages/WorkspacePackagesPage';
import InventoryPage from './features/backoffice/inventory/pages/InventoryPage';
import PurchaseOrdersPage from './features/backoffice/inventory/purchaseOrders/pages/PurchaseOrdersPage';
import HeroPage from './features/backoffice/settings/hero/pages/HeroPage';
import BudgetPage from './features/backoffice/finance/budget/pages/BudgetPage';
import LossesPage from './features/backoffice/inventory/losses/pages/LossesPage';
import SuppliersPage from './features/backoffice/suppliers/pages/SuppliersPage';
import CronJobsPage from './features/backoffice/settings/cronJobs/pages/CronJobsPage';
import CurrenciesPage from './features/backoffice/settings/currencies/pages/CurrenciesPage';
import ReportsPage from './features/backoffice/finance/reports/pages/ReportsPage';
import AnalyticsDashboardPage from './features/backoffice/reports/analytics/pages/AnalyticsDashboardPage';
import FinanceDashboardPage from './features/backoffice/finance/dashboard/pages/FinanceDashboardPage';
import BankReconciliationPage from './features/backoffice/finance/reconciliation/pages/BankReconciliationPage';
import DeliveryCostsPage from './features/backoffice/finance/deliveryCosts/pages/DeliveryCostsPage';
import ProfitReportsPage from './features/backoffice/finance/profit/pages/ProfitReportsPage';
import RefundsPage from './features/backoffice/finance/refunds/pages/RefundsPage';
import PayrollPage from './features/backoffice/finance/payroll/pages/PayrollPage';
import DisplaySettingsPage from './features/backoffice/settings/display/pages/DisplaySettingsPage';
import LandingPromotionSettingsPage from './features/backoffice/settings/landingPromotion/pages/LandingPromotionSettingsPage';
import MailRoutingSettingsPage from './features/backoffice/settings/mailRouting/pages/MailRoutingSettingsPage';
import WorkspaceOrdersPage from './features/backoffice/orders/pages/WorkspaceOrdersPage';
import DeliveryScanPage from './features/backoffice/orders/pages/DeliveryScanPage';
import OrderStatisticsPage from './features/backoffice/orders/pages/OrderStatisticsPage';
import WorkspaceFeedbackInboxPage from './features/backoffice/orders/pages/WorkspaceFeedbackInboxPage';
import WorkspacePaymentsPage from './features/backoffice/payments/pages/WorkspacePaymentsPage';
import WorkspaceActivitiesPage from './features/backoffice/activities/pages/WorkspaceActivitiesPage';
import WorkspacePromotionsPage from './features/backoffice/promotions/pages/WorkspacePromotionsPage';
import WorkspaceReferralsPage from './features/backoffice/referrals/pages/WorkspaceReferralsPage';
import WorkspaceDeliveryBatchesPage from './features/backoffice/deliveryBatches/pages/WorkspaceDeliveryBatchesPage';
import DeliveryPricingSettingsPage from './features/backoffice/settings/deliveryPricing/pages/DeliveryPricingSettingsPageV2';
import AppParametersPage from './features/backoffice/settings/appParameters/pages/AppParametersPage';
import GeoAccessSettingsPage from './features/backoffice/settings/geoAccess/pages/GeoAccessSettingsPage';
import CarrierAccountsSettingsPage from './features/backoffice/settings/carriers/pages/CarrierAccountsSettingsPage';
import ShipFromSettingsPage from './features/backoffice/settings/carriers/pages/ShipFromSettingsPage';
import EmployeeCompleteInvitePage from './pages/EmployeeCompleteInvitePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RecoverAccountPage from './pages/RecoverAccountPage';
import EmployeeAuthPage from './pages/EmployeeAuthPage';
import AgentAuthPage from './pages/AgentAuthPage';
import SecurityBlockPage from './pages/SecurityBlockPage';
import AboutPage from './pages/AboutPage';
import LegalPage from './pages/LegalPage';
import FeedbackPage from './pages/FeedbackPage';
import CookieSettingsPage from './pages/CookieSettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import DepartmentsLandingPage from './pages/DepartmentsLandingPage';
import DepartmentManagementPage from './pages/DepartmentManagementPage';
import WorkspaceSiteContentPage from './features/backoffice/settings/siteContent/pages/WorkspaceSiteContentPage';
import WorkspaceRecommendationSettingsPage from './features/backoffice/settings/recommendations/pages/WorkspaceRecommendationSettingsPage';
import WorkspaceCheckoutMarketingSettingsPage from './features/backoffice/settings/checkoutMarketing/pages/WorkspaceCheckoutMarketingSettingsPage';
import WorkspaceAccountDeletionSettingsPage from './features/backoffice/settings/accountDeletion/pages/WorkspaceAccountDeletionSettingsPage';
import WorkspaceTestimonialsPage from './features/backoffice/settings/testimonials/pages/WorkspaceTestimonialsPage';
import WorkspaceDocumentationPage from './features/backoffice/docs/pages/WorkspaceDocumentationPage';
import TechnicalDocsGatePage from './features/backoffice/docs/pages/TechnicalDocsGatePage';
import { useAuth } from './context/AuthContext';
import { buildLoginUrl, buildStaffLoginUrl } from './utils/authReturnUrl';
import { useGeoAccess } from './context/GeoAccessContext';
import GeoBlockedPage from './pages/GeoBlockedPage';
import { HostRedirect } from './components/routing/HostRedirect';
import {
  getWorkspaceRoutePrefix,
  isSubdomainRoutingEnabled,
  isWorkspaceHost,
  isWorkspacePath as pathIsWorkspaceRoute,
  toCanonicalWorkspacePath,
  wsPath,
} from './utils/siteHosts';

const WORKSPACE_BASE = getWorkspaceRoutePrefix();
const WORKSPACE_CANONICAL_BASE = '/workspace';
const SUBDOMAIN_ROUTING = isSubdomainRoutingEnabled();
const ON_WORKSPACE_HOST = isWorkspaceHost();
const SHOW_STOREFRONT_ROUTES = !SUBDOMAIN_ROUTING || !ON_WORKSPACE_HOST;
const SHOW_WORKSPACE_ROUTES = !SUBDOMAIN_ROUTING || ON_WORKSPACE_HOST;

function workspacePath(relative = ''): string {
  if (!relative || relative === '/') {
    return WORKSPACE_BASE || '/';
  }
  const suffix = relative.startsWith('/') ? relative : `/${relative}`;
  return WORKSPACE_BASE ? `${WORKSPACE_BASE}${suffix}` : suffix;
}
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RequireAuthProps = {
  children: ReactNode;
};

function redirectUnauthenticatedStaff(from: string) {
  const target = buildStaffLoginUrl(from);
  if (target.startsWith('http')) {
    window.location.replace(target);
    return null;
  }
  return <Navigate to={target} replace />;
}

function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={buildLoginUrl(from)} replace />;
  }
  return children;
}

function RequireWorkspaceAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, auth } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    const target = buildStaffLoginUrl(from);
    if (target.startsWith('http')) {
      window.location.replace(target);
      return null;
    }
    return <Navigate to={target} replace />;
  }
  if (auth.type === 'CUSTOMER') {
    return <Navigate to="/account" replace />;
  }
  return children;
}

type RequireModuleProps = {
  children: ReactNode;
  anyOf: string[];
};

type NonSidebarWorkspaceRule = {
  pathPrefix: string;
  requiredAnyOf?: string[];
};

const WORKSPACE_ROUTE_PERMISSION_RULES: NonSidebarWorkspaceRule[] = [
  { pathPrefix: '/workspace/display', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/landing-promotion', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/docs/technical', requiredAnyOf: ['TECH_DOC:READ'] },
  { pathPrefix: '/workspace/docs', requiredAnyOf: ['DOC:READ'] },
  { pathPrefix: '/workspace/site-content', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/recommendations', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/checkout-marketing', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/account-deletion-retention', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/testimonials', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/finance/reconciliation', requiredAnyOf: ['FINANCE:READ', 'BANK_RECONCILIATION:UPDATE'] },
  { pathPrefix: '/workspace/finance/delivery-costs', requiredAnyOf: ['FINANCE:READ', 'DELIVERY_COST:UPDATE'] },
  { pathPrefix: '/workspace/finance/profit', requiredAnyOf: ['PROFIT_REPORT:READ'] },
  { pathPrefix: '/workspace/finance/refunds', requiredAnyOf: ['FINANCE:READ'] },
  { pathPrefix: '/workspace/payroll', requiredAnyOf: ['FINANCE:READ'] },
  { pathPrefix: '/workspace/finance', requiredAnyOf: ['FINANCE:READ'] },
  { pathPrefix: '/workspace/employees', requiredAnyOf: ['EMPLOYEE:READ'] },
  { pathPrefix: '/workspace/shipping-agents', requiredAnyOf: ['EMPLOYEE:READ'] },
  { pathPrefix: '/workspace/roles', requiredAnyOf: ['ROLE:READ'] },
  { pathPrefix: '/workspace/products', requiredAnyOf: ['PRODUCT:READ'] },
  { pathPrefix: '/workspace/expired-products', requiredAnyOf: ['STOCK_LOT:READ', 'LOSS:CREATE'] },
  { pathPrefix: '/workspace/categories', requiredAnyOf: ['CATEGORY:READ'] },
  { pathPrefix: '/workspace/packages', requiredAnyOf: ['PRODUCT:READ'] },
  { pathPrefix: '/workspace/suppliers', requiredAnyOf: ['SUPPLIER:READ'] },
  { pathPrefix: '/workspace/inventory', requiredAnyOf: ['STOCK_LOT:READ', 'PURCHASE:READ'] },
  { pathPrefix: '/workspace/purchases', requiredAnyOf: ['PURCHASE:READ'] },
  { pathPrefix: '/workspace/purchase-orders', requiredAnyOf: ['PURCHASE_ORDER:READ'] },
  { pathPrefix: '/workspace/customers/deletion-archives', requiredAnyOf: ['CUSTOMER:READ'] },
  { pathPrefix: '/workspace/customers', requiredAnyOf: ['CUSTOMER:READ'] },
  { pathPrefix: '/workspace/hero', requiredAnyOf: ['HERO:READ'] },
  { pathPrefix: '/workspace/budget', requiredAnyOf: ['BUDGET_PERIOD:READ'] },
  { pathPrefix: '/workspace/losses', requiredAnyOf: ['LOSS:READ'] },
  { pathPrefix: '/workspace/cron-jobs', requiredAnyOf: ['EXPIRY_ALERT:READ', 'DELIVERY_PRICING:READ', 'ORDER:READ'] },
  { pathPrefix: '/workspace/currencies', requiredAnyOf: ['CURRENCY:READ'] },
  { pathPrefix: '/workspace/reports/analytics', requiredAnyOf: ['ORDER_STATS:READ', 'FINANCE:READ'] },
  { pathPrefix: '/workspace/reports', requiredAnyOf: ['BUDGET_PERIOD:READ', 'BUDGET_REPORT:CREATE'] },
  { pathPrefix: '/workspace/orders', requiredAnyOf: ['ORDER:READ'] },
  { pathPrefix: '/workspace/delivery/scan', requiredAnyOf: ['ORDER:UPDATE'] },
  { pathPrefix: '/workspace/order-statistics', requiredAnyOf: ['ORDER_STATS:READ', 'ORDER:READ'] },
  { pathPrefix: '/workspace/feedback-inbox', requiredAnyOf: ['ORDER:READ'] },
  { pathPrefix: '/workspace/payments', requiredAnyOf: ['PAYMENT:READ'] },
  { pathPrefix: '/workspace/activities', requiredAnyOf: ['ACTIVITY:READ'] },
  { pathPrefix: '/workspace/security/action-audit', requiredAnyOf: ['ACTIVITY:READ'] },
  { pathPrefix: '/workspace/promotions', requiredAnyOf: ['PROMOTION:READ'] },
  { pathPrefix: '/workspace/referrals', requiredAnyOf: ['REFERRAL:READ'] },
  { pathPrefix: '/workspace/delivery-batches', requiredAnyOf: ['DELIVERY_BATCH:READ'] },
  { pathPrefix: '/workspace/mail-routing', requiredAnyOf: ['MAIL_ROUTING:READ'] },
  { pathPrefix: '/workspace/delivery-pricing', requiredAnyOf: ['DELIVERY_PRICING:READ'] },
  { pathPrefix: '/workspace/geo-access', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/carrier-accounts', requiredAnyOf: ['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE'] },
  { pathPrefix: '/workspace/ship-from', requiredAnyOf: ['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE'] },
  { pathPrefix: '/workspace/app-parameters', requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'] },
  { pathPrefix: '/workspace/department-management', requiredAnyOf: ['DEPARTMENT:READ', 'DEPARTMENT:CREATE', 'DEPARTMENT:UPDATE'] },
  { pathPrefix: '/workspace/profile' },
  { pathPrefix: '/workspace/access-pending' },
  { pathPrefix: '/workspace/complete-invite' },
  { pathPrefix: '/workspace/backoffice' },
  { pathPrefix: '/workspace/admin' },
  { pathPrefix: '/workspace' },
];

function hasAnyPermission(
  auth: { type: string | null; permissions?: string[] | null },
  anyOf?: string[],
): boolean {
  if (!anyOf || anyOf.length === 0) return true;
  if (auth.type === 'ADMIN') return true;
  const permissions = auth.permissions || [];
  return anyOf.some((k) => permissions.includes(k));
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  const current = pathname.split('?')[0];
  if (current === prefix) return true;
  return current.startsWith(`${prefix}/`);
}

function LegacyWorkspaceRedirect({ toBase }: { toBase: string }) {
  const location = useLocation();
  return (
    <RequireWorkspaceAuth>
      <Navigate to={`${toBase}${location.search}${location.hash}`} replace />
    </RequireWorkspaceAuth>
  );
}

/** Legacy /workspace/* URLs that only redirect — require staff auth before following the redirect. */
function AuthenticatedWorkspaceNavigate({ to }: { to: string }) {
  return (
    <RequireWorkspaceAuth>
      <Navigate to={wsPath(to)} replace />
    </RequireWorkspaceAuth>
  );
}

/**
 * Invite completion may run logged out with ?token=…; bare /workspace/complete-invite without token is not public.
 */
function RequireWorkspaceInviteAccess({ children }: RequireAuthProps) {
  const { isAuthenticated, auth } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  if (auth.type === 'CUSTOMER') {
    return <Navigate to="/account" replace />;
  }
  if (!isAuthenticated && !token) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return redirectUnauthenticatedStaff(from);
  }
  return children;
}

function pickWorkspaceStartPath(auth: { type: string | null; permissions?: string[] | null }) {
  const isAdmin = auth.type === 'ADMIN';
  const perms = isAdmin ? [
    // Admin: treat as having broad access for routing start.
    // We still enforce real access via backend + permission guards.
    'PRODUCT:READ',
    'CATEGORY:READ',
    'SUPPLIER:READ',
    'PURCHASE_ORDER:READ',
    'PURCHASE:READ',
    'STOCK_LOT:READ',
    'LOSS:READ',
    'BUDGET_PERIOD:READ',
    'BUDGET_REPORT:CREATE',
    'CURRENCY:READ',
    'EXPIRY_ALERT:READ',
    'HERO:READ',
    'EMPLOYEE:READ',
    'CUSTOMER:READ',
    'ROLE:READ',
    'PAYMENT:READ',
    'PROMOTION:READ',
    'REFERRAL:READ',
    'DELIVERY_BATCH:READ',
    'DELIVERY_PRICING:READ',
    'DEPARTMENT:READ',
    'DEPARTMENT:CREATE',
    'DEPARTMENT:UPDATE',
    'DOC:READ',
    'TECH_DOC:READ',
    'MAIL_ROUTING:READ',
    'FINANCE:READ',
    'FINANCE:UPDATE',
    'BANK_RECONCILIATION:UPDATE',
    'PROFIT_REPORT:READ',
    'DELIVERY_COST:UPDATE',
    'ACTIVITY:READ',
  ] : (auth.permissions || []);

  const hasAny = (...keys: string[]) => keys.some((k) => perms.includes(k));

  // Pick the first page the user can access.
  if (hasAny('PRODUCT:READ')) return wsPath('/workspace/products');
  if (hasAny('CATEGORY:READ')) return wsPath('/workspace/categories');
  if (hasAny('SUPPLIER:READ')) return wsPath('/workspace/suppliers');
  if (hasAny('PURCHASE_ORDER:READ')) return wsPath('/workspace/purchase-orders');
  if (hasAny('PURCHASE:READ', 'STOCK_LOT:READ')) return wsPath('/workspace/inventory');
  if (hasAny('LOSS:READ')) return wsPath('/workspace/losses');
  if (hasAny('FINANCE:READ')) return wsPath('/workspace/finance');
  if (hasAny('BUDGET_PERIOD:READ')) return wsPath('/workspace/budget');
  if (hasAny('BUDGET_REPORT:CREATE')) return wsPath('/workspace/reports');
  if (hasAny('CURRENCY:READ')) return wsPath('/workspace/currencies');
  if (hasAny('EXPIRY_ALERT:READ', 'DELIVERY_PRICING:READ', 'ORDER:READ')) return wsPath('/workspace/cron-jobs');
  if (hasAny('HERO:READ')) return wsPath('/workspace/hero');
  if (hasAny('EMPLOYEE:READ')) return wsPath('/workspace/employees');
  if (hasAny('CUSTOMER:READ')) return wsPath('/workspace/customers');
  if (hasAny('ROLE:READ')) return wsPath('/workspace/roles');
  if (hasAny('PAYMENT:READ')) return wsPath('/workspace/payments');
  if (hasAny('PROMOTION:READ')) return wsPath('/workspace/promotions');
  if (hasAny('REFERRAL:READ')) return wsPath('/workspace/referrals');
  if (hasAny('DELIVERY_BATCH:READ')) return wsPath('/workspace/delivery-batches');
  if (hasAny('DEPARTMENT:READ', 'DEPARTMENT:CREATE', 'DEPARTMENT:UPDATE')) return wsPath('/workspace/department-management');
  if (hasAny('DOC:READ')) return wsPath('/workspace/docs');
  if (hasAny('TECH_DOC:READ')) return wsPath('/workspace/docs/technical');
  if (hasAny('DELIVERY_PRICING:READ')) return wsPath('/workspace/delivery-pricing');
  if (hasAny('MAIL_ROUTING:READ')) return wsPath('/workspace/mail-routing');
  if (hasAny('ACTIVITY:READ')) return wsPath('/workspace/activities');

  return wsPath('/workspace/access-pending');
}

function RequirePermission({ children, anyOf }: RequireModuleProps) {
  const { isAuthenticated, auth } = useAuth();
  const location = useLocation();
  if (auth.type === 'CUSTOMER') {
    return <Navigate to="/account" replace />;
  }
  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return redirectUnauthenticatedStaff(from);
  }
  const isAdmin = auth.type === 'ADMIN';
  const permissions = auth.permissions || [];
  const allowed = isAdmin || anyOf.some((k) => permissions.includes(k));
  if (!allowed) {
    return <Navigate to={pickWorkspaceStartPath(auth)} replace />;
  }
  return children;
}

/**
 * Same as `RequirePermission` but, when the user is not authenticated, sends
 * them to the streamlined shipping-agent sign-in page (`/auth/agent`) instead
 * of the regular staff one. This is the route the QR codes on shipping labels
 * deep-link to: agents who tap a label should land on a tiny email + code
 * form, not the full backoffice login.
 */
function RequireAgentOrStaffPermission({ children, anyOf }: RequireModuleProps) {
  const { isAuthenticated, auth } = useAuth();
  const location = useLocation();
  if (auth.type === 'CUSTOMER') {
    return <Navigate to="/account" replace />;
  }
  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={`/auth/agent?returnUrl=${encodeURIComponent(from)}`}
        replace
      />
    );
  }
  const isAdmin = auth.type === 'ADMIN';
  const permissions = auth.permissions || [];
  const allowed = isAdmin || anyOf.some((k) => permissions.includes(k));
  if (!allowed) {
    return <Navigate to={pickWorkspaceStartPath(auth)} replace />;
  }
  return children;
}

function RequireWorkspaceSidebarAccess({ children }: RequireAuthProps) {
  const { isAuthenticated, auth, authorizedFetch } = useAuth();
  const location = useLocation();
  const canonicalPath = toCanonicalWorkspacePath(location.pathname);
  const isWorkspacePath = pathIsWorkspaceRoute(location.pathname);
  const enforceWorkspaceSidebarAccess =
    isAuthenticated && auth.type !== 'CUSTOMER' && isWorkspacePath;
  const [appParamsConfigured, setAppParamsConfigured] = React.useState<
    boolean | null
  >(null);
  const [departmentsConfigured, setDepartmentsConfigured] = React.useState<
    boolean | null
  >(null);
  const [siteContentConfigured, setSiteContentConfigured] = React.useState<
    boolean | null
  >(null);
  const [budgetConfigured, setBudgetConfigured] = React.useState<boolean | null>(
    null,
  );
  const [mailRoutingConfigured, setMailRoutingConfigured] = React.useState<
    boolean | null
  >(null);
  const [deliveryPricingConfigured, setDeliveryPricingConfigured] =
    React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!enforceWorkspaceSidebarAccess) return;
    let cancelled = false;
    const run = async () => {
      try {
        const canAccessDepartmentSetup =
          auth.type === 'ADMIN' ||
          hasAnyPermission(auth, [
            'DEPARTMENT:READ',
            'DEPARTMENT:CREATE',
            'DEPARTMENT:UPDATE',
          ]);
        const [paramsRes, siteContentRes, departmentsRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/settings/app-parameters`, {
            headers: { 'x-department-slug': '' },
          }),
          authorizedFetch(`${API_BASE}/settings/site-content`, {
            headers: { 'x-department-slug': '' },
          }),
          canAccessDepartmentSetup
            ? authorizedFetch(`${API_BASE}/departments/workspace`, {
                headers: { 'x-department-slug': '' },
              })
            : Promise.resolve(
                new Response(JSON.stringify([]), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' },
                }),
              ),
        ]);
        if (!paramsRes.ok || !siteContentRes.ok || !departmentsRes.ok) {
          throw new Error('status');
        }
        const paramsData = (await paramsRes.json()) as { configured?: boolean };
        const departmentsData = (await departmentsRes.json()) as unknown[];
        const siteContentData = (await siteContentRes.json()) as Partial<{
          publicContactCountry: string;
          publicContactCity: string;
          publicContactAddress: string;
          publicContactEmail: string;
          publicContactPhone: string;
        }>;
        const contentReady =
          String(siteContentData.publicContactCountry || '').trim().length > 0 &&
          String(siteContentData.publicContactCity || '').trim().length > 0 &&
          String(siteContentData.publicContactAddress || '').trim().length > 0 &&
          String(siteContentData.publicContactPhone || '').trim().length > 0 &&
          String(siteContentData.publicContactEmail || '')
            .trim()
            .includes('@');
        const departmentsReady = canAccessDepartmentSetup
          ? Array.isArray(departmentsData) && departmentsData.length > 0
          : true;
        let budgetReady = true;
        const canAccessBudget =
          auth.type === 'ADMIN' ||
          hasAnyPermission(auth, [
            'BUDGET_PERIOD:READ',
            'BUDGET_PERIOD:CREATE',
            'BUDGET_PERIOD:UPDATE',
          ]);
        if (canAccessBudget) {
          const budgetRes = await authorizedFetch(
            `${API_BASE}/budget/periods?page=1&pageSize=1`,
          );
          if (!budgetRes.ok) {
            throw new Error('budget_status');
          }
          const budgetData = (await budgetRes.json()) as Partial<{
            items: unknown[];
          }>;
          budgetReady = Array.isArray(budgetData.items) && budgetData.items.length > 0;
        }
        let mailRoutingReady = true;
        const canAccessMailRouting =
          auth.type === 'ADMIN' || hasAnyPermission(auth, ['MAIL_ROUTING:READ']);
        if (canAccessMailRouting) {
          const mailRoutingRes = await authorizedFetch(
            `${API_BASE}/settings/mail-routing`,
            {
              headers: { 'x-department-slug': '' },
            },
          );
          if (!mailRoutingRes.ok) throw new Error('mail_routing_status');
          const mailRoutingData = (await mailRoutingRes.json()) as Partial<{
            config: Record<
              string,
              {
                fromEmails?: string[];
                replyTo?: string;
              }
            >;
          }>;
          const config = mailRoutingData.config || {};
          mailRoutingReady = Object.values(config).some((entry) => {
            const fromCount = Array.isArray(entry?.fromEmails)
              ? entry.fromEmails.filter((x) => String(x).includes('@')).length
              : 0;
            const hasReplyTo = String(entry?.replyTo || '').includes('@');
            return fromCount > 0 || hasReplyTo;
          });
        }
        let deliveryPricingReady = true;
        const canAccessDeliveryPricing =
          auth.type === 'ADMIN' ||
          hasAnyPermission(auth, [
            'DELIVERY_PRICING:READ',
            'DELIVERY_PRICING:UPDATE',
          ]);
        if (canAccessDeliveryPricing) {
          const deliveryPricingRes = await authorizedFetch(
            `${API_BASE}/delivery-pricing`,
            {
              headers: { 'x-department-slug': '' },
            },
          );
          if (!deliveryPricingRes.ok) throw new Error('delivery_pricing_status');
          const deliveryPricingData = (await deliveryPricingRes.json()) as Partial<{
            pricing: {
              storeCountry?: string;
              countries?: Array<{ countryCode?: string; enabled?: boolean }>;
            };
          }>;
          const storeCountry = String(
            deliveryPricingData.pricing?.storeCountry || '',
          )
            .trim()
            .toUpperCase();
          const countries = Array.isArray(deliveryPricingData.pricing?.countries)
            ? deliveryPricingData.pricing?.countries || []
            : [];
          const hasAtLeastOneCountry = countries.length > 0;
          const hasStoreCountryPricing = countries.some((c) => {
            const code = String(c.countryCode || '')
              .trim()
              .toUpperCase();
            if (!code || code !== storeCountry) return false;
            return c.enabled !== false;
          });
          deliveryPricingReady =
            hasAtLeastOneCountry && Boolean(storeCountry) && hasStoreCountryPricing;
        }
        if (!cancelled) {
          setDepartmentsConfigured(departmentsReady);
          setAppParamsConfigured(Boolean(paramsData.configured));
          setSiteContentConfigured(contentReady);
          setBudgetConfigured(budgetReady);
          setMailRoutingConfigured(mailRoutingReady);
          setDeliveryPricingConfigured(deliveryPricingReady);
        }
      } catch {
        if (!cancelled) {
          setDepartmentsConfigured(true);
          setAppParamsConfigured(true);
          setSiteContentConfigured(true);
          setBudgetConfigured(true);
          setMailRoutingConfigured(true);
          setDeliveryPricingConfigured(true);
        }
      }
    };
    void run();
    const interval = window.setInterval(() => {
      void run();
    }, 4000);
    const onFocus = () => {
      void run();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.permissions,
    auth.type,
    authorizedFetch,
    enforceWorkspaceSidebarAccess,
    location.pathname,
  ]);

  if (!enforceWorkspaceSidebarAccess) return children;

  const matchedRule = [...WORKSPACE_ROUTE_PERMISSION_RULES]
    .sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)
    .find((rule) => pathMatchesPrefix(canonicalPath, rule.pathPrefix));

  if (!matchedRule) return children;
  const allowed = hasAnyPermission(auth, matchedRule.requiredAnyOf);

  if (!allowed) {
    return <Navigate to={pickWorkspaceStartPath(auth)} replace />;
  }

  const isAppParametersPage =
    canonicalPath === '/workspace/app-parameters' ||
    canonicalPath.startsWith('/workspace/app-parameters/');
  const isDepartmentManagementPage =
    canonicalPath === '/workspace/department-management' ||
    canonicalPath.startsWith('/workspace/department-management/');
  const isSiteContentPage =
    canonicalPath === '/workspace/site-content' ||
    canonicalPath.startsWith('/workspace/site-content/');
  const isBudgetPage =
    canonicalPath === '/workspace/budget' ||
    canonicalPath.startsWith('/workspace/budget/');
  const isMailRoutingPage =
    canonicalPath === '/workspace/mail-routing' ||
    canonicalPath.startsWith('/workspace/mail-routing/');
  const isDeliveryPricingPage =
    canonicalPath === '/workspace/delivery-pricing' ||
    canonicalPath.startsWith('/workspace/delivery-pricing/');
  if (departmentsConfigured === false && !isDepartmentManagementPage) {
    return <Navigate to={wsPath('/workspace/department-management')} replace />;
  }
  if (appParamsConfigured === false && !isAppParametersPage) {
    if (!isDepartmentManagementPage) {
      return <Navigate to={wsPath('/workspace/app-parameters')} replace />;
    }
  }
  if (appParamsConfigured === true && siteContentConfigured === false) {
    if (
      !isSiteContentPage &&
      !isAppParametersPage &&
      !isDepartmentManagementPage
    ) {
      return <Navigate to={wsPath('/workspace/site-content')} replace />;
    }
  }
  if (
    appParamsConfigured === true &&
    siteContentConfigured === true &&
    budgetConfigured === false
  ) {
    if (
      !isBudgetPage &&
      !isSiteContentPage &&
      !isAppParametersPage &&
      !isDepartmentManagementPage
    ) {
      return <Navigate to={wsPath('/workspace/budget')} replace />;
    }
  }
  if (
    appParamsConfigured === true &&
    siteContentConfigured === true &&
    budgetConfigured === true &&
    mailRoutingConfigured === false
  ) {
    if (
      !isMailRoutingPage &&
      !isBudgetPage &&
      !isSiteContentPage &&
      !isAppParametersPage &&
      !isDepartmentManagementPage
    ) {
      return <Navigate to={wsPath('/workspace/mail-routing')} replace />;
    }
  }
  if (
    appParamsConfigured === true &&
    siteContentConfigured === true &&
    budgetConfigured === true &&
    mailRoutingConfigured === true &&
    deliveryPricingConfigured === false
  ) {
    if (
      !isDeliveryPricingPage &&
      !isMailRoutingPage &&
      !isBudgetPage &&
      !isSiteContentPage &&
      !isAppParametersPage &&
      !isDepartmentManagementPage
    ) {
      return <Navigate to={wsPath('/workspace/delivery-pricing')} replace />;
    }
  }

  return children;
}

function RequireDepartmentExists({ children }: RequireAuthProps) {
  const { departmentSlug } = useParams();
  const [status, setStatus] = React.useState<'checking' | 'allowed' | 'blocked'>('checking');

  React.useEffect(() => {
    const slug = String(departmentSlug || '').trim().toLowerCase();
    if (!slug) {
      setStatus('blocked');
      return;
    }
    let cancelled = false;
    setStatus('checking');
    const run = async () => {
      try {
        const res = await fetch(`${API_BASE}/departments/public/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          setStatus(res.ok ? 'allowed' : 'blocked');
        }
      } catch {
        if (!cancelled) {
          setStatus('blocked');
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug]);

  if (status === 'checking') return null;
  if (status === 'blocked') return <NotFoundPage />;
  return children;
}

function DepartmentSlugGate({ children }: RequireAuthProps) {
  const location = useLocation();
  const { isAuthenticated, auth } = useAuth();
  const [status, setStatus] = React.useState<'checking' | 'allowed' | 'blocked'>('allowed');

  React.useEffect(() => {
    if (ON_WORKSPACE_HOST) return;
    const firstSegment = location.pathname.split('/').filter(Boolean)[0] || '';
    const reserved = new Set([
      '',
      'about',
      'legal',
      'feedback',
      'cookie-settings',
      'auth',
      'verify-email',
      'forgot-password',
      'reset-password',
      'recover-account',
      'account',
      'workspace',
      'department-management',
      'departments',
      'products',
      'packages',
      'cart',
      'checkout',
    ]);
    if (reserved.has(firstSegment)) {
      setStatus('allowed');
      return;
    }

    let cancelled = false;
    setStatus('checking');
    const slug = firstSegment;
    const run = async () => {
      try {
        // Staff routes must verify against workspace-visible departments (enforces access scope).
        if (isAuthenticated && auth.type !== 'CUSTOMER') {
          const res = await fetch(`${API_BASE}/departments/workspace`, {
            headers: auth.accessToken
              ? { Authorization: `Bearer ${auth.accessToken}` }
              : undefined,
          });
          if (res.ok) {
            const rows = (await res.json()) as Array<{ slug?: string }>;
            const exists = rows.some((row) => String(row.slug || '').toLowerCase() === slug.toLowerCase());
            if (!cancelled) {
              setStatus(exists ? 'allowed' : 'blocked');
            }
            return;
          }
        }

        // Public storefront validation (active departments only).
        const res = await fetch(`${API_BASE}/departments/public/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          setStatus(res.ok ? 'allowed' : 'blocked');
        }
      } catch {
        if (!cancelled) {
          setStatus('blocked');
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [auth.accessToken, auth.type, isAuthenticated, location.pathname]);

  if (ON_WORKSPACE_HOST) {
    return children;
  }

  if (status === 'checking') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" aria-hidden />
      </div>
    );
  }
  if (status === 'blocked') {
    return <NotFoundPage />;
  }
  return children;
}

/** Legacy URLs used `/{department}/cart`; cart is store-wide at `/cart`. */
function RedirectLegacyDepartmentCartToGlobalCart() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/cart', search, hash }} replace />;
}

/** On workspace host, old /workspace/... bookmarks map to host-native paths. */
function LegacyWorkspacePrefixRedirect() {
  const location = useLocation();
  const target = wsPath(location.pathname);
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

function App() {
  const { auth } = useAuth();
  const { blocked, loading: geoLoading } = useGeoAccess();
  const [workspaceScopeKey, setWorkspaceScopeKey] = React.useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });

  React.useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScopeKey(readScope());
    };
    window.addEventListener('workspace-department-scope:change', handleScopeChange as EventListener);
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener('workspace-department-scope:change', handleScopeChange as EventListener);
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  if (!geoLoading && blocked) {
    return <GeoBlockedPage />;
  }
  return (
    <DepartmentSlugGate>
      <RequireWorkspaceSidebarAccess>
        <Routes key={workspaceScopeKey}>
        <Route path="/auth/security-block" element={<SecurityBlockPage />} />
        {SHOW_STOREFRONT_ROUTES && (
          <Route path="/auth" element={<AuthPage />} />
        )}
        {SHOW_WORKSPACE_ROUTES && SUBDOMAIN_ROUTING && (
          <Route path="/auth" element={<StaffAuthPage />} />
        )}
        {!SUBDOMAIN_ROUTING && (
          <Route path="/auth/staff" element={<StaffAuthPage />} />
        )}
        <Route path="/auth/employee" element={<EmployeeAuthPage />} />
        {SHOW_WORKSPACE_ROUTES && (
          <Route path="/auth/agent" element={<AgentAuthPage />} />
        )}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/recover-account" element={<RecoverAccountPage />} />
        {SHOW_STOREFRONT_ROUTES && (
        <>
        <Route path="/" element={<HomePage />} />
        <Route path="/departments" element={<DepartmentsLandingPage />} />
        <Route path="/products" element={<ProductsCatalogPage />} />
        <Route path="/products/:id" element={<ProductDetailsPage />} />
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/packages/:slug" element={<PackageDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route
          path="/:departmentSlug"
          element={(
            <RequireDepartmentExists>
              <HomePage />
            </RequireDepartmentExists>
          )}
        />
        <Route
          path="/:departmentSlug/products"
          element={(
            <RequireDepartmentExists>
              <ProductsCatalogPage />
            </RequireDepartmentExists>
          )}
        />
        <Route
          path="/:departmentSlug/packages"
          element={(
            <RequireDepartmentExists>
              <PackagesPage />
            </RequireDepartmentExists>
          )}
        />
        <Route
          path="/:departmentSlug/packages/:slug"
          element={(
            <RequireDepartmentExists>
              <PackageDetailPage />
            </RequireDepartmentExists>
          )}
        />
        <Route
          path="/:departmentSlug/products/:id"
          element={(
            <RequireDepartmentExists>
              <ProductDetailsPage />
            </RequireDepartmentExists>
          )}
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/cookie-settings" element={<CookieSettingsPage />} />
        <Route
          path="/:departmentSlug/cart"
          element={<RedirectLegacyDepartmentCartToGlobalCart />}
        />
        <Route
          path="/checkout"
          element={(
            <CheckoutPage />
          )}
        />
        <Route path="/:departmentSlug/checkout" element={<Navigate to="/checkout" replace />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/account"
        element={(
          <RequireAuth>
            <CustomerAccountPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/account/orders"
        element={(
          <RequireAuth>
            <AccountOrdersPage />
          </RequireAuth>
        )}
      />
      <Route
        path="/account/referrals"
        element={(
          <RequireAuth>
            <AccountReferralsPage />
          </RequireAuth>
        )}
      />
        </>
        )}
      {SHOW_WORKSPACE_ROUTES && (
      <>
      <Route
        path={workspacePath('/department-management')}
        element={(
          <RequirePermission anyOf={['DEPARTMENT:READ', 'DEPARTMENT:CREATE', 'DEPARTMENT:UPDATE']}>
            <DepartmentManagementPage />
          </RequirePermission>
        )}
      />
      {!SUBDOMAIN_ROUTING && (
      <Route
        path="/department-management"
        element={<Navigate to={wsPath('/workspace/department-management')} replace />}
      />
      )}
      {SUBDOMAIN_ROUTING && !ON_WORKSPACE_HOST && (
        <Route path="/department-management" element={<HostRedirect />} />
      )}
      {SUBDOMAIN_ROUTING && !ON_WORKSPACE_HOST && (
        <Route path="/workspace/*" element={<HostRedirect />} />
      )}
      {ON_WORKSPACE_HOST && (
        <Route path="/workspace/*" element={<LegacyWorkspacePrefixRedirect />} />
      )}
      {!SUBDOMAIN_ROUTING && (
      <Route path="/workspace/*" element={<NotFoundPage />} />
      )}
      <Route
        path={workspacePath("/profile")}
        element={(
          <RequireWorkspaceAuth>
            <EmployeeProfilePage />
          </RequireWorkspaceAuth>
        )}
      />
      <Route
        path={workspacePath("/access-pending")}
        element={(
          <RequireWorkspaceAuth>
            <WorkspaceAccessPendingPage />
          </RequireWorkspaceAuth>
        )}
      />
      <Route
        path={workspacePath("/display")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <DisplaySettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/landing-promotion")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <LandingPromotionSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/docs")}
        element={(
          <RequirePermission anyOf={['DOC:READ']}>
            <WorkspaceDocumentationPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/docs/:docId")}
        element={(
          <RequirePermission anyOf={['DOC:READ']}>
            <WorkspaceDocumentationPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/docs/technical")}
        element={(
          <RequirePermission anyOf={['TECH_DOC:READ']}>
            <TechnicalDocsGatePage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/docs/technical/:docId")}
        element={(
          <RequirePermission anyOf={['TECH_DOC:READ']}>
            <TechnicalDocsGatePage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/site-content")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <WorkspaceSiteContentPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/recommendations")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <WorkspaceRecommendationSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/checkout-marketing")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <WorkspaceCheckoutMarketingSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/account-deletion-retention")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <WorkspaceAccountDeletionSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/testimonials")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <WorkspaceTestimonialsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/payroll")}
        element={(
          <RequirePermission anyOf={['FINANCE:READ']}>
            <PayrollPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/finance")}
        element={(
          <RequirePermission anyOf={['FINANCE:READ']}>
            <FinanceDashboardPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/finance/reconciliation")}
        element={(
          <RequirePermission anyOf={['FINANCE:READ', 'BANK_RECONCILIATION:UPDATE']}>
            <BankReconciliationPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/finance/delivery-costs")}
        element={(
          <RequirePermission anyOf={['FINANCE:READ', 'DELIVERY_COST:UPDATE']}>
            <DeliveryCostsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/finance/profit")}
        element={(
          <RequirePermission anyOf={['PROFIT_REPORT:READ']}>
            <ProfitReportsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/finance/refunds")}
        element={(
          <RequirePermission anyOf={['FINANCE:READ']}>
            <RefundsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath()}
        element={(
          <RequireWorkspaceAuth>
            <Navigate to={pickWorkspaceStartPath(auth)} replace />
          </RequireWorkspaceAuth>
        )}
      />
      <Route
        path={workspacePath("/employees")}
        element={(
          <RequirePermission anyOf={['EMPLOYEE:READ']}>
            <EmployeesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/shipping-agents")}
        element={(
          <RequirePermission anyOf={['EMPLOYEE:READ']}>
            <WorkspaceShippingAgentsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/employees/:employeeId")}
        element={(
          <RequirePermission anyOf={['EMPLOYEE:READ']}>
            <EmployeeDetailsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/roles")}
        element={(
          <RequirePermission anyOf={['ROLE:READ']}>
            <RolesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/roles/:roleId")}
        element={(
          <RequirePermission anyOf={['ROLE:READ']}>
            <RolePermissionsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/products")}
        element={(
          <RequirePermission anyOf={['PRODUCT:READ']}>
            <ProductsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/expired-products")}
        element={(
          <RequirePermission anyOf={['STOCK_LOT:READ', 'LOSS:CREATE']}>
            <ExpiredProductsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/categories")}
        element={(
          <RequirePermission anyOf={['CATEGORY:READ']}>
            <CategoriesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/packages")}
        element={(
          <RequirePermission anyOf={['PRODUCT:READ']}>
            <WorkspacePackagesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/suppliers")}
        element={(
          <RequirePermission anyOf={['SUPPLIER:READ']}>
            <SuppliersPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/inventory")}
        element={(
          <RequirePermission anyOf={['STOCK_LOT:READ', 'PURCHASE:READ']}>
            <InventoryPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/purchases")}
        element={(
          <RequirePermission anyOf={['PURCHASE:READ']}>
            <InventoryPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/purchase-orders")}
        element={(
          <RequirePermission anyOf={['PURCHASE_ORDER:READ']}>
            <PurchaseOrdersPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/customers/deletion-archives/:archiveId")}
        element={(
          <RequirePermission anyOf={['CUSTOMER:READ']}>
            <CustomerArchiveDetailPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/customers/deletion-archives")}
        element={(
          <RequirePermission anyOf={['CUSTOMER:READ']}>
            <CustomerArchivesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/customers/:id")}
        element={(
          <RequirePermission anyOf={['CUSTOMER:READ']}>
            <CustomerDetailPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/customers")}
        element={(
          <RequirePermission anyOf={['CUSTOMER:READ']}>
            <CustomersPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/hero")}
        element={(
          <RequirePermission anyOf={['HERO:READ']}>
            <HeroPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/budget")}
        element={(
          <RequirePermission anyOf={['BUDGET_PERIOD:READ']}>
            <BudgetPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/losses")}
        element={(
          <RequirePermission anyOf={['LOSS:READ']}>
            <LossesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/cron-jobs")}
        element={(
          <RequirePermission anyOf={['EXPIRY_ALERT:READ', 'DELIVERY_PRICING:READ', 'ORDER:READ']}>
            <CronJobsPage />
          </RequirePermission>
        )}
      />
      <Route path={workspacePath("/expiry-alerts")} element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/cron-jobs`} />} />
      <Route
        path={workspacePath("/currencies")}
        element={(
          <RequirePermission anyOf={['CURRENCY:READ']}>
            <CurrenciesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/reports")}
        element={(
          <RequirePermission anyOf={['BUDGET_PERIOD:READ', 'BUDGET_REPORT:CREATE']}>
            <ReportsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/reports/analytics")}
        element={(
          <RequirePermission anyOf={['ORDER_STATS:READ', 'FINANCE:READ']}>
            <AnalyticsDashboardPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/orders")}
        element={(
          <RequirePermission anyOf={['ORDER:READ']}>
            <WorkspaceOrdersPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/delivery/scan")}
        element={(
          <RequireAgentOrStaffPermission anyOf={['ORDER:UPDATE']}>
            <DeliveryScanPage />
          </RequireAgentOrStaffPermission>
        )}
      />
      <Route
        path={workspacePath("/order-statistics")}
        element={(
          <RequirePermission anyOf={['ORDER_STATS:READ', 'ORDER:READ']}>
            <OrderStatisticsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/feedback-inbox")}
        element={(
          <RequirePermission anyOf={['ORDER:READ']}>
            <WorkspaceFeedbackInboxPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/payments")}
        element={(
          <RequirePermission anyOf={['PAYMENT:READ']}>
            <WorkspacePaymentsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/activities")}
        element={(
          <RequirePermission anyOf={['ACTIVITY:READ']}>
            <WorkspaceActivitiesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/security/action-audit")}
        element={(
          <RequirePermission anyOf={['ACTIVITY:READ']}>
            <WorkspaceActivitiesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/promotions")}
        element={(
          <RequirePermission anyOf={['PROMOTION:READ']}>
            <WorkspacePromotionsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/referrals")}
        element={(
          <RequirePermission anyOf={['REFERRAL:READ']}>
            <WorkspaceReferralsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/delivery-batches")}
        element={(
          <RequirePermission anyOf={['DELIVERY_BATCH:READ']}>
            <WorkspaceDeliveryBatchesPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/mail-routing")}
        element={(
          <RequirePermission anyOf={['MAIL_ROUTING:READ']}>
            <MailRoutingSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/delivery-pricing")}
        element={(
          <RequirePermission anyOf={['DELIVERY_PRICING:READ']}>
            <DeliveryPricingSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/app-parameters")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <AppParametersPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/geo-access")}
        element={(
          <RequirePermission anyOf={['UI_SETTING:READ', 'UI_SETTING:UPDATE']}>
            <GeoAccessSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/carrier-accounts")}
        element={(
          <RequirePermission anyOf={['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE']}>
            <CarrierAccountsSettingsPage />
          </RequirePermission>
        )}
      />
      <Route
        path={workspacePath("/ship-from")}
        element={(
          <RequirePermission anyOf={['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE']}>
            <ShipFromSettingsPage />
          </RequirePermission>
        )}
      />

      {/* Redirect legacy base path */}
      <Route path="/workspace/backoffice" element={<AuthenticatedWorkspaceNavigate to={WORKSPACE_CANONICAL_BASE} />} />
      <Route path="/workspace/backoffice/employees" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/employees`} />} />
      <Route path="/workspace/backoffice/products" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/products`} />} />
      <Route path="/workspace/backoffice/packages" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/packages`} />} />
      <Route path="/workspace/backoffice/categories" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/categories`} />} />
      <Route path="/workspace/backoffice/suppliers" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/suppliers`} />} />
      <Route path="/workspace/backoffice/inventory" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/inventory`} />} />
      <Route path="/workspace/backoffice/purchases" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/purchases`} />} />
      <Route path="/workspace/backoffice/purchase-orders" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/purchase-orders`} />} />
      <Route path="/workspace/backoffice/customers" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/customers`} />} />
      <Route path="/workspace/backoffice/hero" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/hero`} />} />
      <Route path="/workspace/backoffice/budget" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/budget`} />} />
      <Route path="/workspace/backoffice/losses" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/losses`} />} />
      <Route path="/workspace/backoffice/expiry-alerts" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/cron-jobs`} />} />
      <Route path="/workspace/backoffice/cron-jobs" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/cron-jobs`} />} />
      <Route path="/workspace/backoffice/currencies" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/currencies`} />} />
      <Route path="/workspace/backoffice/reports" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/reports`} />} />
      <Route path="/workspace/backoffice/display" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/display`} />} />
      <Route path="/workspace/backoffice/orders" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/orders`} />} />
      <Route path="/workspace/backoffice/order-statistics" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/order-statistics`} />} />

      {/* Backward-compatible redirects from old /workspace/admin/* URLs */}
      <Route path="/workspace/admin" element={<AuthenticatedWorkspaceNavigate to={WORKSPACE_CANONICAL_BASE} />} />
      <Route path="/workspace/admin/employees" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/employees`} />} />
      <Route path="/workspace/admin/products" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/products`} />} />
      <Route path="/workspace/admin/packages" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/packages`} />} />
      <Route path="/workspace/admin/categories" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/categories`} />} />
      <Route path="/workspace/admin/suppliers" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/suppliers`} />} />
      <Route path="/workspace/admin/inventory" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/inventory`} />} />
      <Route path="/workspace/admin/purchases" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/purchases`} />} />
      <Route path="/workspace/admin/purchase-orders" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/purchase-orders`} />} />
      <Route path="/workspace/admin/customers" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/customers`} />} />
      <Route path="/workspace/admin/hero" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/hero`} />} />
      <Route path="/workspace/admin/budget" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/budget`} />} />
      <Route path="/workspace/admin/losses" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/losses`} />} />
      <Route path="/workspace/admin/expiry-alerts" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/cron-jobs`} />} />
      <Route path="/workspace/admin/cron-jobs" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/cron-jobs`} />} />
      <Route path="/workspace/admin/currencies" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/currencies`} />} />
      <Route path="/workspace/admin/reports" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/reports`} />} />
      <Route path="/workspace/admin/display" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/display`} />} />
      <Route path="/workspace/admin/orders" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/orders`} />} />
      <Route path="/workspace/admin/order-statistics" element={<AuthenticatedWorkspaceNavigate to={`${WORKSPACE_CANONICAL_BASE}/order-statistics`} />} />
      <Route
        path={workspacePath('/complete-invite')}
        element={(
          <RequireWorkspaceInviteAccess>
            <EmployeeCompleteInvitePage />
          </RequireWorkspaceInviteAccess>
        )}
      />

      {/* Legacy redirects for older workspace settings URLs */}
      <Route path="/workspace/settings/site-content" element={<LegacyWorkspaceRedirect toBase={wsPath('/workspace/site-content')} />} />

      </>
      )}

      {/* Catch-all for unknown routes */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </RequireWorkspaceSidebarAccess>
    </DepartmentSlugGate>
  );
}

export default App;

